import React, { useState, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import logoOrig from '../img/tmis_logo.png';
import logoWhite from '../img/tmis_logo_w.png';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePopupCard, PopupCard, PopupCardLink, PopupCardButton } from './FlyoutCard';

const NAV_ITEMS = [
  { label: 'Dashboard',          to: '/trainer',             end: true, icon: 'bi-speedometer2' },
  { label: 'Schedules',          to: '/trainer/schedules',              icon: 'bi-calendar3' },
  { label: 'Trainer Observation', to: '/trainer/observation',           icon: 'bi-clipboard-check' },
  { label: 'Assessment',         to: '/trainer/assessment',             icon: 'bi-journal-check' },
  { label: 'Marking',            to: '/trainer/component-marks',        icon: 'bi-diagram-3' },
  { label: 'Claim',              to: '/trainer/claim',                  icon: 'bi-receipt' },
];

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
}

export default function TrainerLayout({ children }) {
  const { user, avatarColor, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate          = useNavigate();
  const location          = useLocation();
  const isDark            = theme === 'dark';
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sms_trainer_rail_collapsed') === '1');
  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sms_trainer_rail_collapsed', next ? '1' : '0');
      return next;
    });
  };

  const railRef = useRef(null);
  const getSidebarRect = useCallback(() => railRef.current ? railRef.current.getBoundingClientRect() : null, []);
  const acctMenu = usePopupCard({ getSidebarRect, mode: 'account' });

  const navItems = NAV_ITEMS;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="trn-app">
      <aside ref={railRef} className={`trn-rail ${collapsed ? 'collapsed' : ''}`} aria-label="Trainer portal navigation">
        <div
          className="trn-rail-head sms-sidebar-head"
          onClick={collapsed ? toggleCollapsed : undefined}
          role={collapsed ? 'button' : undefined}
          tabIndex={collapsed ? 0 : undefined}
          onKeyDown={collapsed ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCollapsed(); } }) : undefined}
          aria-label={collapsed ? 'Expand sidebar' : undefined}
        >
          <img src="/favicon.svg" alt="TMIS SMS" className="trn-brand-collapsed-icon" />
          <div className="trn-brand">
            <img src={isDark ? logoWhite : logoOrig} alt="TMIS SMS" className="trn-brand-logo" />
          </div>
          <button
            className="trn-collapse-btn sms-sidebar-collapse-btn"
            onClick={e => { e.stopPropagation(); toggleCollapsed(); }}
            aria-pressed={collapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i className="bi bi-chevron-bar-left" aria-hidden="true" />
          </button>
        </div>

        <nav className="trn-nav sms-panel-body" aria-label="Primary">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-label={item.label}
              className={({ isActive }) => `trn-nav-item sms-panel-item ${isActive ? 'active' : ''}`}
            >
              <span className="sms-nav-icon-col"><i className={`bi ${item.icon}`} aria-hidden="true" /></span>
              <span className="sms-panel-item-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="trn-rail-foot sms-panel-foot-wrap">
          <div className="sms-sidebar-theme-toggle" role="group" aria-label="Theme">
            <button type="button" className={`sms-sidebar-theme-seg ${!isDark ? 'active' : ''}`}
              onClick={() => isDark && toggle()} aria-pressed={!isDark}>
              <i className="bi bi-sun-fill" aria-hidden="true" />
              <span className="sms-sidebar-theme-seg-label">Light</span>
            </button>
            <button type="button" className={`sms-sidebar-theme-seg ${isDark ? 'active' : ''}`}
              onClick={() => !isDark && toggle()} aria-pressed={isDark}>
              <i className="bi bi-moon-stars-fill" aria-hidden="true" />
              <span className="sms-sidebar-theme-seg-label">Dark</span>
            </button>
          </div>
          <button type="button" className="sms-sidebar-theme-collapsed-btn" onClick={toggle}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDark ? 'Light mode' : 'Dark mode'}>
            <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="sms-panel-foot-trigger"
            onClick={e => acctMenu.toggle('account', e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={acctMenu.openId === 'account'}
            aria-label={`Account menu for ${user?.full_name || 'account'}`}
          >
            <div className="sms-panel-foot-avatar" style={{ background: avatarColor || undefined }}>
              {initials(user?.full_name)}
            </div>
            <div className="sms-panel-foot-user">
              <div className="sms-panel-foot-name">{user?.full_name}</div>
              <div className="sms-panel-foot-email">{user?.username}</div>
            </div>
            <i className="bi bi-three-dots-vertical sms-panel-foot-more" aria-hidden="true" />
          </button>
        </div>

        <div className="trn-rail-mobile-actions">
          <button className="trn-icon-btn" onClick={toggle} aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
            <i className={`bi ${isDark ? 'bi-sun' : 'bi-moon'}`} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="trn-mobile-avatar-btn"
            onClick={e => acctMenu.toggle('account', e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={acctMenu.openId === 'account'}
            aria-label={`Account menu for ${user?.full_name || 'account'}`}
          >
            <div className="sms-panel-foot-avatar" style={{ background: avatarColor || undefined }}>
              {initials(user?.full_name)}
            </div>
          </button>
        </div>
      </aside>

      <div className="trn-main">
        <main id="main-content" className="trn-main-content" tabIndex="-1" key={location.pathname}>
          {children}
        </main>
      </div>

      <nav className="trn-bottom-nav" aria-label="Primary mobile navigation">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `trn-bottom-nav-item ${isActive ? 'active' : ''}`}
          >
            <i className={`bi ${item.icon}`} aria-hidden="true" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {acctMenu.openId === 'account' && (
        <PopupCard
          pos={acctMenu.pos}
          cardRef={acctMenu.cardRef}
          label={user?.full_name}
          onMouseEnter={acctMenu.cancelClose}
          onMouseLeave={acctMenu.scheduleClose}
        >
          <PopupCardLink
            to="/trainer/profile"
            icon="bi-person-badge"
            label="My Profile"
            active={location.pathname === '/trainer/profile'}
            onClick={acctMenu.closeNow}
          />
          <PopupCardButton
            icon="bi-box-arrow-right"
            label="Log out"
            danger
            onClick={() => { acctMenu.closeNow(); handleLogout(); }}
          />
        </PopupCard>
      )}
    </div>
  );
}
